import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ulid } from 'ulid'
import {
  orderShelfFor,
  type Order,
  type OrderShelf,
  type OrderStatus,
  type OrderActionStatus,
  type RefundOrderRequest,
} from '@random-vinyl/backend/schemas'
import { ordersService, type ListOrdersParams } from '@/api/orders'
import { toErrorCode } from '@/lib/error-code'

/**
 * The Bestellingen (orders) workbench store. Mirrors the catalogue store's shape:
 * a server-backed list with a shelf bucket (open/closed) + optional status filter,
 * plus mutation actions that replace the affected order in the loaded list so the
 * queue updates without a full refetch. The drawer fetches its own live message
 * thread; this store owns only the list + write actions.
 */
export const useOrdersStore = defineStore('orders', () => {
  const orders = ref<Order[]>([])
  const loading = ref(false)
  const loadingMore = ref(false)
  const error = ref<string | null>(null)
  const cursor = ref<string | null>(null)
  // 'open' = needs work (default queue); 'closed' = archive.
  const shelf = ref<OrderShelf>('open')
  const statusFilter = ref<OrderStatus | null>(null)
  // A drawer write (status/message/note) is in flight — drives button disabling.
  const transitioning = ref(false)
  const lastLoadedAt = ref<number | null>(null)

  /**
   * Open-queue size for the nav badge + catalogue KPI. `actionable` excludes
   * awaiting_payment (nothing staff can do about an unpaid webshop attempt —
   * it either pays or the sweeper cancels it). null = never loaded, so the
   * surfaces can show a placeholder instead of a lying 0. Best-effort: a
   * failed refresh keeps the last known number — a badge is a hint, not a
   * source of truth (the Bestellingen page is).
   */
  const openCount = ref<number | null>(null)
  const actionableCount = ref<number | null>(null)
  /** Orders shipped or collected this calendar month — the KPI's delta line. */
  const completedThisMonth = ref<number | null>(null)
  const countLoadedAt = ref<number | null>(null)
  let countInFlight: Promise<void> | null = null
  // A forced refresh raised WHILE a read is in flight: that read was issued
  // before the write that forced it, so its answer cannot serve — remember to
  // run one more once it lands.
  let countForceQueued = false
  // Bumped by reset(): a request still in flight when auth.logout() runs must
  // not write the PREVIOUS tenant's numbers into the fresh store.
  let countGeneration = 0

  /**
   * How long a count stays fresh. App.vue refreshes on EVERY route change, and
   * staff bounce between Catalogus / Kassa / Bakken dozens of times a shift —
   * without this, each hop costs a Lambda invocation and a full GSI6 sweep for
   * a number that has not moved. `force` is for the writes that DID move it
   * (applyOrderUpdate).
   */
  const COUNT_TTL_MS = 30_000

  async function refreshOpenCount(force = false): Promise<void> {
    // One in-flight request at a time: rapid navigation otherwise overlaps
    // calls whose responses can land out of order and pin a stale number.
    // A FORCED call can't simply ride that request, though — it was issued
    // before the write that forced it, so adopting its answer would pin the
    // pre-action number AND refresh the TTL on top of it, hiding the change
    // for a further 30 s. Queue one instead.
    if (countInFlight) {
      if (force) countForceQueued = true
      return countInFlight
    }
    if (!force && countLoadedAt.value !== null && Date.now() - countLoadedAt.value < COUNT_TTL_MS) {
      return
    }
    const generation = countGeneration
    countInFlight = (async () => {
      try {
        const counts = await ordersService.openCount()
        if (generation !== countGeneration) return
        openCount.value = counts.open
        actionableCount.value = counts.actionable
        completedThisMonth.value = counts.completedThisMonth
        countLoadedAt.value = Date.now()
      } catch {
        // Keep the previous value; the next navigation refreshes again.
      } finally {
        if (generation === countGeneration) {
          countInFlight = null
          if (countForceQueued) {
            countForceQueued = false
            void refreshOpenCount(true)
          }
        }
      }
    })()
    return countInFlight
  }

  function buildParams(): ListOrdersParams {
    const p: ListOrdersParams = { shelf: shelf.value }
    if (statusFilter.value) p.status = statusFilter.value
    return p
  }

  async function fetchOrders(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const page = await ordersService.listPage(buildParams())
      orders.value = page.items
      cursor.value = page.nextCursor ?? null
      lastLoadedAt.value = Date.now()
    } catch (err) {
      // Surface to the banner AND re-throw, mirroring the catalogue store: a caller
      // that awaits (a retry wrapper, a test) sees the rejection; fire-and-forget
      // sites opt out with `.catch(() => {})` (see OrdersView).
      error.value = toErrorCode(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loadMore(): Promise<void> {
    if (!cursor.value || loadingMore.value) return
    loadingMore.value = true
    // Clear any stale banner from a previous failed load so a later success
    // doesn't leave an error showing (mirrors fetchOrders).
    error.value = null
    try {
      const page = await ordersService.listPage({ ...buildParams(), cursor: cursor.value })
      orders.value = [...orders.value, ...page.items]
      cursor.value = page.nextCursor ?? null
    } catch (err) {
      error.value = toErrorCode(err)
      throw err
    } finally {
      loadingMore.value = false
    }
  }

  function setShelf(s: OrderShelf): void {
    if (s === shelf.value) return
    shelf.value = s
    statusFilter.value = null
    void fetchOrders()
  }

  function setStatusFilter(s: OrderStatus | null): void {
    statusFilter.value = s
    void fetchOrders()
  }

  /**
   * Reflect a drawer mutation in the loaded list: replace the order in place when
   * it still belongs to the current view, otherwise drop it (e.g. an order shipped
   * from the `open` queue moves to `closed`).
   */
  function applyOrderUpdate(order: Order): void {
    const belongs =
      orderShelfFor(order.status) === shelf.value &&
      (!statusFilter.value || order.status === statusFilter.value)
    if (belongs) {
      const i = orders.value.findIndex(o => o.id === order.id)
      if (i >= 0) orders.value[i] = order
      else orders.value = [order, ...orders.value]
    } else {
      orders.value = orders.value.filter(o => o.id !== order.id)
    }
    // A drawer action may have opened/closed the order — keep the badge
    // honest. `force`, because this is exactly the write the TTL must not hide.
    void refreshOpenCount(true)
  }

  async function changeStatus(channelOrderId: string, status: OrderActionStatus): Promise<Order> {
    transitioning.value = true
    try {
      // Fresh idempotency key per status change — the backend dedupes a duplicate
      // delivery so the buyer isn't notified twice (the status post is non-idempotent).
      const order = await ordersService.updateStatus(channelOrderId, status, ulid())
      applyOrderUpdate(order)
      return order
    } finally {
      transitioning.value = false
    }
  }

  async function sendMessage(
    channelOrderId: string,
    body: string,
    status?: OrderActionStatus
  ): Promise<Order> {
    transitioning.value = true
    try {
      // Fresh idempotency key per send — see changeStatus.
      const order = await ordersService.sendMessage(channelOrderId, body, status, ulid())
      applyOrderUpdate(order)
      return order
    } finally {
      transitioning.value = false
    }
  }

  /**
   * Refund an order, in full or line by line, and bring the queue up to date.
   *
   * The response order is applied as usual — but for a DISCOGS order that row is
   * deliberately untouched by a refund (the poller rebuilds it every poll), so
   * the caller must re-read `ordersService.refundState` to see the new totals.
   * Doing it here would make every caller pay for a fetch the drawer already
   * owns; `OrderDrawer` does it in the same action.
   */
  async function refund(channelOrderId: string, body: RefundOrderRequest): Promise<Order> {
    transitioning.value = true
    try {
      const order = await ordersService.refund(channelOrderId, body)
      applyOrderUpdate(order)
      // A full refund closes the order, which moves it between queue buckets.
      void refreshOpenCount(true)
      return order
    } finally {
      transitioning.value = false
    }
  }

  async function saveNote(channelOrderId: string, note: string | null): Promise<Order> {
    transitioning.value = true
    try {
      const order = await ordersService.setNote(channelOrderId, note)
      applyOrderUpdate(order)
      return order
    } finally {
      transitioning.value = false
    }
  }

  function reset(): void {
    orders.value = []
    loading.value = false
    loadingMore.value = false
    error.value = null
    cursor.value = null
    shelf.value = 'open'
    statusFilter.value = null
    transitioning.value = false
    lastLoadedAt.value = null
    openCount.value = null
    actionableCount.value = null
    completedThisMonth.value = null
    countLoadedAt.value = null
    // Drop the in-flight request AND invalidate it: without this, a logout
    // mid-request left the promise parked here (so every later refresh
    // early-returned it) and then wrote the old tenant's counts on resolve.
    countInFlight = null
    countForceQueued = false
    countGeneration++
  }

  return {
    orders,
    loading,
    loadingMore,
    error,
    cursor,
    shelf,
    statusFilter,
    transitioning,
    lastLoadedAt,
    openCount,
    actionableCount,
    completedThisMonth,
    refreshOpenCount,
    fetchOrders,
    loadMore,
    setShelf,
    setStatusFilter,
    changeStatus,
    sendMessage,
    saveNote,
    refund,
    reset,
  }
})
